import React, { useState, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, ZoomInIcon, ZoomOutIcon, PrinterIcon } from 'lucide-react';
import { useTheme } from '../providers/ThemeProvider';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
export const InventionNo2Viewer: React.FC = () => {
  const navigate = useNavigate();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [zoom, setZoom] = useState(100);
  // Detect mobile device
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  // PDF URL for Invention No. 2 BWV 773
  const pdfUrl = 'https://www.mutopiaproject.org/ftp/BachJS/BWV773/bach-invention-02/bach-invention-02-let.pdf';
  // Use mobile-friendly PDF parameters on mobile devices
  const pdfViewUrl = isMobile ? `${pdfUrl}#view=Fit&zoom=page-width` : `${pdfUrl}#view=FitH`;
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 10, 200));
  };
  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 10, 50));
  };
  const handleDownload = () => {
    // Download the actual PDF
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'Bach - Invention No. 2 BWV 773.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handlePrint = () => {
    window.print();
  };
  return <div className={`min-h-screen ${isDark ? 'bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]' : 'bg-gray-50'}`} data-id="element-4189">
      <Navbar data-id="element-4190" />

      {/* Wrapper with top padding to account for fixed Navbar */}
      <div className="pt-20" data-id="element-4191">
        {/* Hero Banner with Image */}
        <div className="relative w-full" style={{
        minHeight: '320px'
      }} data-id="element-4192">
          <img src="https://images.unsplash.com/photo-1552422535-c45813c61732?w=400&h=300&fit=crop" alt="Invention No. 2" className="w-full h-64 md:h-80 object-cover" data-id="element-4193" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/70" data-id="element-4194" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8" data-id="element-4195">
            <div className="max-w-7xl mx-auto" data-id="element-4196">
              <button onClick={() => navigate(-1)} className="mb-4 flex items-center text-white/90 hover:text-white transition-colors" data-id="element-4197">
                <ArrowLeftIcon className="h-5 w-5 mr-2" data-id="element-4198" />
                Back to Library
              </button>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2" data-id="element-4199">
                Invention No. 2 in C minor
              </h1>
              <p className="text-lg md:text-xl text-white/90" data-id="element-4200">BWV 773</p>
              <p className="text-base md:text-lg text-white/80 mt-1" data-id="element-4201">
                Johann Sebastian Bach
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className={`sticky top-20 z-10 ${isDark ? 'bg-navy-800/95 backdrop-blur-sm border-b border-white/10' : 'bg-white/95 backdrop-blur-sm border-b border-gray-200'} shadow-sm`} data-id="element-4202">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between" data-id="element-4203">
          <div className="flex items-center space-x-2" data-id="element-4204">
            {!isMobile && <>
                <button onClick={handleZoomOut} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom out" data-id="element-4205">
                  <ZoomOutIcon className="h-5 w-5" data-id="element-4206" />
                </button>
                <span className={`text-sm font-medium px-3 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4207">
                  {zoom}%
                </span>
                <button onClick={handleZoomIn} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom in" data-id="element-4208">
                  <ZoomInIcon className="h-5 w-5" data-id="element-4209" />
                </button>
              </>}
          </div>
          <div className="flex items-center space-x-2" data-id="element-4210">
            <button onClick={handlePrint} className={`flex items-center justify-center px-4 py-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white border border-navy-700' : 'hover:bg-gray-100 text-gray-800 border border-gray-200'}`} data-id="element-4211">
              <PrinterIcon className="h-4 w-4 sm:mr-2" data-id="element-4212" />
              <span className="hidden sm:inline" data-id="element-4213">Print</span>
            </button>
            <button onClick={handleDownload} className="flex items-center justify-center px-4 py-2 border-2 border-green-500 text-green-500 bg-transparent hover:bg-green-500/10 rounded-lg transition-colors" data-id="element-4214">
              <DownloadIcon className="h-4 w-4 sm:mr-2" data-id="element-4215" />
              <span className="hidden sm:inline" data-id="element-4216">Download PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* Sheet Music Viewer */}
      <main className="max-w-7xl mx-auto px-4 py-8" data-id="element-4217">
        <div className={`rounded-xl overflow-hidden shadow-lg ${isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-4218">
          {/* PDF Viewer - Embedded */}
          <div className="w-full" style={{
          height: 'calc(100vh - 300px)',
          minHeight: '600px'
        }} data-id="element-4219">
            <iframe src={pdfViewUrl} className="w-full h-full" title="Invention No. 2 Sheet Music" style={{
            border: 'none',
            ...(isMobile ? {} : {
              transform: `scale(${zoom / 100})`,
              transformOrigin: 'top center',
              transition: 'transform 0.2s ease'
            })
          }} data-id="element-4220" />
          </div>
        </div>

        {/* Piece Information */}
        <div className={`mt-8 rounded-xl p-6 ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-4221">
          <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4222">
            About This Piece
          </h2>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-4223">
            <div data-id="element-4224">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4225">
                Composition Details
              </h3>
              <dl className="space-y-2" data-id="element-4226">
                <div data-id="element-4227">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4228">
                    Composer
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4229">
                    Johann Sebastian Bach
                  </dd>
                </div>
                <div data-id="element-4230">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4231">
                    Year Composed
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4232">
                    c. 1720-1723
                  </dd>
                </div>
                <div data-id="element-4233">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4234">
                    Form
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4235">
                    Two-Part Invention
                  </dd>
                </div>
                <div data-id="element-4236">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4237">
                    Key
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4238">
                    C minor
                  </dd>
                </div>
                <div data-id="element-4239">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4240">
                    Catalog
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4241">
                    BWV 773
                  </dd>
                </div>
              </dl>
            </div>
            <div data-id="element-4242">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4243">
                Performance Notes
              </h3>
              <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-4244">
                The second of Bach's fifteen Two-Part Inventions, this piece in
                C minor showcases the composer's mastery of counterpoint and
                inventive development. Written as pedagogical works for his
                eldest son Wilhelm Friedemann, the Inventions demonstrate how to
                play cleanly in two voices and develop musical ideas
                systematically. This invention features a distinctive chromatic
                subject that weaves through both hands, creating a dialogue of
                imitation and inversion. The minor key lends the piece a
                serious, introspective character, while the constant interplay
                between the two voices demands clarity and independence in both
                hands. The work exemplifies Bach's ability to create compelling
                music from simple materials through rigorous contrapuntal
                technique, making it both an essential study piece and a
                rewarding performance work.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-4245" />
    </div>;
};